import { Injectable } from '@angular/core';
import { ApiService } from '../../core/api.service';
import { SyncCryptService } from '../../core/crypt/sync-crypt.service';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../reducers';
import * as fromFileList from '../../reducers/file-list.reducer';
import * as FileListActions from '../../actions/file-list.actions';
import { ERROR_CODES, ErrCode, PathListApiOutput, EncFile } from '../../shared/models';
import { BehaviorSubject, Observable } from 'rxjs';
import { LoggerService } from '../../core/logger.service';
import { FileItemService } from '../../file/file-item.service';
import { FileItem } from 'src/app/shared/models/files.model';

@Injectable({
  providedIn: 'root',
})
export class StarredListService {
  private totalFavoriteSubject = new BehaviorSubject<number>(0);
  public total_favorite$: Observable<number> = this.totalFavoriteSubject.asObservable();

  constructor(
    private api: ApiService,
    private crypt: SyncCryptService,
    private store: Store<fromRoot.State>,
    private log: LoggerService,
    private fileItemService: FileItemService,
  ) {}

  public async addTag(syncId: number[], userId: number): Promise<PathListApiOutput> {
    try {
      const d = await this.api.execute<PathListApiOutput>('addtag', {
        sync_id: syncId,
        user_id: userId,
      });
      this.store.dispatch(
        new FileListActions.FileListTotalFavoriteAction(this.totalFavoriteSubject.value + syncId.length),
      );
      this.totalFavoriteSubject.next(this.totalFavoriteSubject.value + syncId.length);
      return d;
    } catch (err) {
      throw new ErrCode(ERROR_CODES.STAR_ADD_FAILED, 'Failed to add star');
    }
  }

  public showFavorites(syncId: number): void {
    this.store.dispatch(new FileListActions.FileListShowFavoritesAction({ syncId }));
  }

  public getFavoritsList(): Observable<fromFileList.State> {
    return this.store.select(fromRoot.getFavoritsListState);
  }
  public async showFavoritesList(syncId: number, pathlist: FileItem[] = []): Promise<FileItem[]> {
    try {
      const d = await this.api.execute<PathListApiOutput>('gettags', {
        sync_id: syncId,
      });
      const cwd = await this.fileItemService.format(d.cwd);
      this.store.dispatch(new FileListActions.FileListSetCwdAction(cwd));
      if (d.parents) {
        const parents: EncFile[] = [];
        for (let i = 0, len = d.parents.length; i < len; i++) {
          let name: string;
          if (d.parents[i].metaname == 'web' || d.parents[i].metaname == 'Web Uploads') {
            name = 'Vault';
          } else {
            try {
              name = await this.crypt.filenameDecrypt(d.parents[i].metaname);
            } catch (ex) {
              name = d.parents[i].metaname;
            }
          }
          parents.push(
            new EncFile({
              loc_id: d.parents[i].loc_id,
              metaname: d.parents[i].metaname,
              name: name,
            }),
          );
        }
      }
      if (d.pathlist && d.pathlist.length) {
        for (let i = 0, len = d.pathlist.length; i < len; i++) {
          if (
            pathlist.some((file: FileItem) => {
              return d.pathlist[i].sync_id === file.sync_id;
            })
          ) {
            this.log.error(`Duplicate sync ids '${d.pathlist[i].sync_id}' in pid '${syncId}'`);
            throw new ErrCode(
              ERROR_CODES.STARRED_ACCESS_ERROR,
              'Unable to access your starred files/folders right now. Please retry later.',
            );
          }
          const file = d.pathlist[i];
          file.rootpath = d.pathlist[i].rootpath;
          pathlist.push(await this.fileItemService.format(file));
        }
        return pathlist;
      } else {
        return pathlist;
      }
    } catch (error) {
      throw ErrCode.fromException(error);
    }
  }

  public async removeTag(syncId: number[]): Promise<PathListApiOutput> {
    try {
      const d = await this.api.execute<PathListApiOutput>('removetag', {
        sync_id: syncId,
      });
      this.store.dispatch(
        new FileListActions.FileListTotalFavoriteAction(this.totalFavoriteSubject.value - syncId.length),
      );
      this.totalFavoriteSubject.next(this.totalFavoriteSubject.value - syncId.length);
      return d;
    } catch (err) {
      throw new ErrCode(ERROR_CODES.STAR_REMOVE_FAILED, 'Failed to remove star');
    }
  }

  public async setTotalFavorite(totalFavorite: number): Promise<void> {
    this.store.dispatch(new FileListActions.FileListTotalFavoriteAction(totalFavorite));
    this.totalFavoriteSubject.next(totalFavorite);
  }
}
